/**
 * The typed wrapper every feature calls. Features never touch `fetch`, never
 * touch `rawRequest`, and never import the generated OpenAPI modules directly.
 *
 * What this layer owns:
 *   • `Authorization: Bearer <access token>` from memory
 *   • ONE silent refresh per failing request, behind the shared mutex
 *   • a GET retry policy — twice, jittered, on a network error / 502 / 504
 *   • `Idempotency-Key` on the ONE money-moving mutation, and nowhere else
 *
 * What it deliberately does not own: authorization. The bundle is public by
 * construction; every read and write is authorized by the owning backend on
 * the request itself, and the origin a call arrives from is never an
 * authorization input.
 */
import { clearSession, readAccessToken } from '@shared/auth/session';
import { currentPath, loginPathFor } from '@shared/auth/next';
import { refreshOnce } from '@shared/auth/refresh';

import { ApiError } from './envelope';
import { rawRequest, type HttpMethod, type HttpRequest } from './http';

/**
 * The only endpoint on any service that accepts `Idempotency-Key`. Sending the
 * header anywhere else is a defect, not a harmless extra — no other route
 * stores a keyed response, so it reads as an unknown header on a write that
 * will simply execute twice.
 */
const IDEMPOTENT_ROUTE = /^\/plans\/[^/]+:subscribe$/;

const GET_RETRY_ATTEMPTS = 2;
const RETRYABLE_STATUS = new Set([502, 504]);

export interface RequestOptions extends Omit<HttpRequest, 'method' | 'path'> {
  /** Client-generated ULID. Permitted on the subscribe mutation only. */
  idempotencyKey?: string;
  /** Escape hatch for the auth screens, which have no session to refresh. */
  skipAuthRefresh?: boolean;
}

function jitteredDelay(attempt: number): number {
  const base = 250 * 2 ** attempt;
  return base + Math.random() * base;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function isRetryable(error: unknown): boolean {
  return error instanceof ApiError && (error.status === 0 || RETRYABLE_STATUS.has(error.status));
}

function authHeaders(extra?: Record<string, string>): Record<string, string> {
  const token = readAccessToken();
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
}

/** Session is gone for good: drop the token and hand the reader the wall. */
function endSessionAndRedirect(): never {
  clearSession();
  window.location.replace(loginPathFor(currentPath()));
  throw new ApiError({
    status: 401,
    errorCode: 'AUTH_SESSION_ENDED',
    message: 'انتهت الجلسة.',
    xRequestId: null,
  });
}

async function send<T>(method: HttpMethod, path: string, options: RequestOptions = {}): Promise<T> {
  const { idempotencyKey, skipAuthRefresh, headers, ...rest } = options;

  if (idempotencyKey && !IDEMPOTENT_ROUTE.test(path)) {
    throw new Error(
      `Idempotency-Key was passed to ${method} ${path}. Only the subscribe mutation accepts it.`,
    );
  }

  const request: HttpRequest = {
    method,
    path,
    ...rest,
    headers: authHeaders({
      ...headers,
      ...(idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {}),
    }),
  };

  const attempt = async (): Promise<T> => rawRequest<T>(request);

  const withGetRetries = async (): Promise<T> => {
    // Mutations NEVER auto-retry. A 429 is not retried either: the caller backs
    // off on `data.retry_after`, which the resend bar is expressed through.
    if (method !== 'GET') return attempt();

    let lastError: unknown;
    for (let index = 0; index <= GET_RETRY_ATTEMPTS; index += 1) {
      try {
        return await attempt();
      } catch (error) {
        lastError = error;
        if (!isRetryable(error) || index === GET_RETRY_ATTEMPTS) break;
        await sleep(jitteredDelay(index));
      }
    }
    throw lastError;
  };

  try {
    return await withGetRetries();
  } catch (error) {
    if (!(error instanceof ApiError) || error.status !== 401 || skipAuthRefresh) throw error;

    // First 401: one silent refresh through the mutex, then one retry.
    const refreshed = await refreshOnce();
    if (!refreshed) endSessionAndRedirect();

    // The STALE Authorization has to come off before the fresh one goes on.
    // `authHeaders` spreads its argument last, so passing the original headers
    // straight back in would let the expired token overwrite the new one — the
    // replay would then 401 a second time and eject a reader whose refresh had
    // actually succeeded. Everything else the caller set (`Idempotency-Key`
    // above all) has to survive, so the header set is carried, not rebuilt.
    const carried = { ...request.headers };
    delete carried.Authorization;
    request.headers = authHeaders(carried);
    try {
      return await rawRequest<T>(request);
    } catch (retryError) {
      // Second 401: the refresh itself is no longer trusted.
      if (retryError instanceof ApiError && retryError.status === 401) endSessionAndRedirect();
      throw retryError;
    }
  }
}

export const apiClient = {
  get: <T>(path: string, options?: RequestOptions) => send<T>('GET', path, options),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    send<T>('POST', path, { ...options, body }),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    send<T>('PUT', path, { ...options, body }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    send<T>('PATCH', path, { ...options, body }),
  delete: <T>(path: string, options?: RequestOptions) => send<T>('DELETE', path, options),
};

export type ApiClient = typeof apiClient;
